/**
 * Mounts the shared query client and restores the signed-in user's cache from
 * IndexedDB. Keyed by uid: with no user nothing is restored or saved; a new
 * build (`buildId`) discards whatever an older one wrote.
 */
import {
  PersistQueryClientProvider,
  type PersistQueryClientOptions
} from "@tanstack/react-query-persist-client";
import { useMemo, type FC, type ReactNode } from "react";
import { useSelector } from "react-redux";

import { buildId } from "@/generated/buildInfo";
import { lawyerSelectors } from "@/redux/lawyer/selectors";

import {
  PERSIST_MAX_AGE_MS,
  createPersister,
  shouldPersistQuery
} from "./persister";
import { queryClient } from "./queryClient";

type Props = { children: ReactNode };

/** A persister that saves nothing, for the signed-out state. */
const NOOP_PERSISTER: PersistQueryClientOptions["persister"] = {
  persistClient: async () => undefined,
  restoreClient: async () => undefined,
  removeClient: async () => undefined
};

export const QueryProvider: FC<Props> = ({ children }) => {
  const uid = useSelector(lawyerSelectors.selectUid);
  const persistOptions = useMemo<
    Omit<PersistQueryClientOptions, "queryClient">
  >(
    () => ({
      persister: uid ? createPersister(uid) : NOOP_PERSISTER,
      maxAge: PERSIST_MAX_AGE_MS,
      buster: buildId,
      dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery }
    }),
    [uid]
  );
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions}
    >
      {children}
    </PersistQueryClientProvider>
  );
};
